import type { Suggestion } from '../types/firestore';
import type { SuggestionViewMode } from './suggestionWorkflow';
import { getSuggestionDefaultView, resolveSuggestionViewMode } from './suggestionWorkflow';

export interface SuggestionHostSelectionState {
  suggestionId: string;
  view: SuggestionViewMode;
}

export interface ResolveSuggestionHostSelectionInput {
  visibleSuggestions: Suggestion[];
  currentSelection: SuggestionHostSelectionState;
  querySelection: SuggestionHostSelectionState;
  defaultView: SuggestionViewMode;
  allowQuerySelection?: boolean;
  autoSelectSuggestionId?: string;
  fallbackToFirstVisible?: boolean;
}

export interface QuerySelectionPendingOutsideVisibleInput {
  suggestions: Suggestion[];
  visibleSuggestions: Suggestion[];
  querySelection: SuggestionHostSelectionState;
  lastAppliedQuerySelectionKey: string;
  loading?: boolean;
}

export const getSuggestionIdentity = (
  suggestion: Pick<Suggestion, 'id' | 'suggestionId' | 'uid'>
): string => suggestion.id || suggestion.suggestionId || suggestion.uid || '';

export const isSuggestionInVisibleSet = (
  visibleSuggestions: Suggestion[],
  suggestionId: string
): boolean =>
  Boolean(suggestionId) &&
  visibleSuggestions.some((suggestion) => getSuggestionIdentity(suggestion) === suggestionId);

export const getSuggestionHostSelectionKey = ({
  suggestionId,
  view,
}: SuggestionHostSelectionState): string => `${suggestionId}|${view}`;

export const isQuerySelectionPendingOutsideVisible = ({
  suggestions,
  visibleSuggestions,
  querySelection,
  lastAppliedQuerySelectionKey,
  loading = false,
}: QuerySelectionPendingOutsideVisibleInput): boolean => {
  if (!querySelection.suggestionId || loading) {
    return false;
  }

  if (getSuggestionHostSelectionKey(querySelection) === lastAppliedQuerySelectionKey) {
    return false;
  }

  const queryTargetExists = suggestions.some(
    (suggestion) => getSuggestionIdentity(suggestion) === querySelection.suggestionId
  );

  return (
    queryTargetExists && !isSuggestionInVisibleSet(visibleSuggestions, querySelection.suggestionId)
  );
};

export const resolveSuggestionHostSelection = ({
  visibleSuggestions,
  currentSelection,
  querySelection,
  defaultView,
  allowQuerySelection = true,
  autoSelectSuggestionId = '',
  fallbackToFirstVisible = true,
}: ResolveSuggestionHostSelectionInput): SuggestionHostSelectionState => {
  const firstVisibleSuggestion = visibleSuggestions[0] || null;
  const queryVisibleSuggestion =
    visibleSuggestions.find(
      (suggestion) => getSuggestionIdentity(suggestion) === querySelection.suggestionId
    ) || null;
  const currentVisibleSuggestion =
    visibleSuggestions.find(
      (suggestion) => getSuggestionIdentity(suggestion) === currentSelection.suggestionId
    ) || null;
  const autoSelectedSuggestion =
    visibleSuggestions.find(
      (suggestion) => getSuggestionIdentity(suggestion) === autoSelectSuggestionId
    ) || null;

  if (allowQuerySelection && queryVisibleSuggestion) {
    return {
      suggestionId: querySelection.suggestionId,
      view: resolveSuggestionViewMode(queryVisibleSuggestion, querySelection.view, defaultView),
    };
  }

  if (currentVisibleSuggestion) {
    return {
      suggestionId: currentSelection.suggestionId,
      view: resolveSuggestionViewMode(currentVisibleSuggestion, currentSelection.view, defaultView),
    };
  }

  if (autoSelectedSuggestion) {
    return {
      suggestionId: getSuggestionIdentity(autoSelectedSuggestion),
      view: getSuggestionDefaultView(autoSelectedSuggestion, defaultView),
    };
  }

  if (fallbackToFirstVisible && firstVisibleSuggestion) {
    return {
      suggestionId: getSuggestionIdentity(firstVisibleSuggestion),
      view: getSuggestionDefaultView(firstVisibleSuggestion, defaultView),
    };
  }

  return {
    suggestionId: '',
    view: defaultView,
  };
};
